import React, { useEffect, useState } from 'react';

import { Group, MenuItemProps, Text } from '@mantine/core';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useDocuments from 'hooks/document/useDocuments';
import { CellProps } from 'react-table';
import { DocumentList } from 'types';
import { removeEmpty } from 'utils/functions';

import { DocumentTemplateSearchFilters } from '../types';

interface ItemProps extends MenuItemProps {
  title: string;
  onClick?: () => void;
  divider?: 'top' | 'bottom';
  'data-test'?: string;
}

const DocumentTemplatesTable = ({
  searchFilters,
  handleEditDocument,
  handleDeleteDocument,
  permission = true,
}: {
  searchFilters: DocumentTemplateSearchFilters;
  handleEditDocument: (type: 'waiver' | 'contract', id: string) => void;
  handleDeleteDocument: (id: string) => void;
  permission?: boolean;
}): JSX.Element => {
  const [tableData, setTableData] = useState<DocumentList[]>([]);
  const [ordering, setOrdering] = useState('');
  const [ascending, setAscending] = useState(true);

  const strippedFilters = removeEmpty(searchFilters);
  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useDocuments({
      ...strippedFilters,
      ordering: ascending ? ordering : `-${ordering}`,
      isTemplate: true,
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => !a);
  };

  const getMenuItems = (document: DocumentList): ItemProps[] => {
    const items = [
      {
        title: 'Edit',
        onClick: () => handleEditDocument(document.type, document.documentId),
        color: 'black',
        'data-cy': 'editButton',
      },
      {
        title: 'Delete',
        onClick: () => handleDeleteDocument(document.documentId),
        color: 'red',
        'data-cy': 'deleteButton',
      },
    ];
    return items;
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Description',
      accessor: 'description' as const,
      Cell: ({ row }: CellProps<DocumentList>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleEditDocument(row.original.type, row.original.documentId)}
        >
          {row.original.description}
        </Text>
      ),
    },
    {
      Header: 'Type',
      accessor: 'type' as const,
    },
    {
      id: 'actions',
      disableSortBy: true,
      Cell: ({ row }: CellProps<DocumentList>) => (
        <Group position='right' px='xs'>
          <Menu
            position='bottom-end'
            items={getMenuItems(row.original)}
            disabled={!permission}
            data-cy='rowButtonDocumentTemplate'
          />
        </Group>
      ),
    },
  ];

  return (
    <Table
      tableData={tableData}
      columns={columns}
      isSuccess={isSuccess}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      clickableHeaders={['description']}
      handleHeaderClick={handleHeaderClick}
      ordering={ordering}
      ascending={ascending}
    />
  );
};

export default DocumentTemplatesTable;
